import React, { useState } from "react";
import { CalendarDays, CheckCircle2, ChevronLeft, ChevronRight, Users, Flame, XCircle, MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLead } from "@/components/site/LeadContext";
import { Reveal, Overline } from "@/components/site/Reveal";
import { CONTACT } from "@/data/content";

const WD = ["D", "S", "T", "Q", "Q", "S", "S"];
const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const hash = (y, m, d) => Math.abs((y * 372 + m * 31 + d) * 2654435761);

function isClosed(month, day) {
  if (month === 11 && [24, 25, 31].includes(day)) return true;
  if (month === 0 && day === 1) return true;
  if (month === 10 && day === 21) return true;
  return false;
}

// Cadastre aqui as datas realmente lotadas no formato AAAA-MM-DD.
// Exemplo: "2026-11-15", "2026-12-20"
const datasLotadas = [
  "2026-11-21",
  "2026-11-01",
  "2026-10-31",
];

// Apenas as datas acima ficam lotadas; as demais mantêm a lógica anterior.
function statusFor(y, m, d) {
  const data = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  if (datasLotadas.includes(data)) return { type: "full", vagas: 0 };

  const dow = new Date(y, m, d).getDay();
  const weekend = dow === 0 || dow === 6;
  const peak = m === 10 || m === 11 || m === 0; // Nov, Dez, Jan
  const h = hash(y, m, d);
  if (peak && weekend) {
    const r = h % 10;
    if (r < 7) return { type: "few", vagas: 6 + (h % 5) };
  }
  return { type: "available", vagas: 8 + (h % 13) };
}

export default function Availability() {
  const { openLead } = useLead();
  const today = startOfDay(new Date());
  const maxDate = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());

  const [view, setView] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [selected, setSelected] = useState(null);

  const atStart = view.y === today.getFullYear() && view.m === today.getMonth();
  const atEnd = view.y === maxDate.getFullYear() && view.m === maxDate.getMonth();

  const shift = (delta) => {
    const d = new Date(view.y, view.m + delta, 1);
    setView({ y: d.getFullYear(), m: d.getMonth() });
    setSelected(null);
  };

  const first = new Date(view.y, view.m, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <section className="relative bg-white py-24 md:py-32" data-testid="availability-section">
      <div className="mx-auto max-w-5xl px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <Reveal>
            <Overline className="text-sun">Nossa agenda</Overline>
            <p className="mt-4 rounded-2xl bg-ocean-mid/10 px-4 py-3 text-base font-semibold text-ocean-mid">
              Não sabe ainda a data que quer vir? Sem problemas! Você garante seu
              ingresso agora e agenda depois.
            </p>
            <h2 className="mt-5 font-display text-4xl font-extrabold uppercase leading-[1.04] tracking-tight text-brand-navy sm:text-5xl">
              Quer ver se a data está livre antes de comprar?
            </h2>
            <p className="mt-5 text-base leading-relaxed text-brand-navy/75">
              Dá uma olhada na nossa agenda. Tem muita data disponível — e você compra
              com tranquilidade agora, no menor preço, e escolhe o dia com calma depois
              (dentro de 1 ano). Algumas datas de temporada já estão com poucas vagas
              ou lotadas, então não deixe para a última hora.
            </p>
            <ul className="mt-6 space-y-2.5">
              {["Escolha a data depois da compra", "Inclusive finais de semana e feriados", "Reagendamento gratuito"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-brand-navy">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-ocean-mid" />
                  <span className="font-medium">{t}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-ocean-mid/15 bg-ocean-light/40 p-5 shadow-xl shadow-ocean-mid/10 sm:p-7">
              <div className="mb-5 flex items-center justify-between">
                <button onClick={() => shift(-1)} disabled={atStart} className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-brand-navy shadow-sm transition-opacity disabled:opacity-30" data-testid="availability-prev" aria-label="Mês anterior">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-5 w-5 text-ocean-mid" />
                  <span className="font-display text-base font-extrabold text-brand-navy">{MONTHS[view.m]} {view.y}</span>
                </div>
                <button onClick={() => shift(1)} disabled={atEnd} className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-brand-navy shadow-sm transition-opacity disabled:opacity-30" data-testid="availability-next" aria-label="Próximo mês">
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1.5 text-center">
                {WD.map((w, i) => (
                  <div key={i} className="pb-1 text-xs font-bold text-brand-navy/40">{w}</div>
                ))}
                {cells.map((d, i) => {
                  if (d === null) return <div key={i} />;
                  const date = new Date(view.y, view.m, d);
                  const past = date < today;
                  const beyond = date > maxDate;
                  const closed = isClosed(view.m, d);
                  const disabled = past || beyond || closed;
                  const st = disabled ? null : statusFor(view.y, view.m, d);
                  const isSel = selected && selected.y === view.y && selected.m === view.m && selected.d === d;

                  let cls = "text-slate-300";
                  if (closed) cls = "bg-slate-100 text-slate-300 line-through";
                  else if (st?.type === "available") cls = "bg-white text-brand-navy ring-1 ring-ocean-mid/25 hover:bg-ocean-mid/10";
                  else if (st?.type === "few") cls = "bg-amber-100 text-amber-700 ring-1 ring-amber-400 hover:bg-amber-200";
                  else if (st?.type === "full") cls = "bg-brand-red/15 text-brand-red/70 ring-1 ring-brand-red/30";
                  if (isSel) cls = st?.type === "full" ? "bg-red-600 text-white ring-2 ring-red-700" : "cta-sun text-white ring-0";

                  return (
                    <button
                      key={i}
                      disabled={disabled}
                      onClick={() => setSelected({ y: view.y, m: view.m, d, ...st })}
                      data-testid={!disabled ? `availability-day-${d}` : undefined}
                      className={`relative flex aspect-square items-center justify-center rounded-lg text-sm font-semibold transition-colors ${cls}`}
                    >
                      {d}
                      {!isSel && st?.type === "few" && <span className="absolute bottom-0.5 h-1 w-1 rounded-full bg-amber-500" />}
                      {!isSel && st?.type === "full" && <span className="absolute bottom-0.5 h-1 w-1 rounded-full bg-brand-red" />}
                    </button>
                  );
                })}
              </div>

              <AnimatePresence mode="wait">
                {selected && (
                  <motion.div
                    key={`${selected.y}-${selected.m}-${selected.d}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                    className="mt-5"
                    data-testid="availability-vagas"
                  >
                    {selected.type === "full" ? (
                      <a href={CONTACT.whatsappFloatLink} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-2xl bg-brand-red/10 px-4 py-3 text-center text-sm font-bold text-brand-red hover:bg-brand-red/15" data-testid="availability-full-contact">
                        <XCircle className="h-4 w-4" />
                        {String(selected.d).padStart(2, "0")}/{String(view.m + 1).padStart(2, "0")} está lotada.
                      </a>
                    ) : selected.type === "few" ? (
                      <div className="flex items-center justify-center gap-2 rounded-2xl bg-amber-100 px-4 py-3 text-center text-sm font-bold text-amber-700">
                        <Flame className="h-4 w-4" />
                        Corre! Restam só {selected.vagas} vagas em {String(selected.d).padStart(2, "0")}/{String(view.m + 1).padStart(2, "0")}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2 rounded-2xl bg-ocean-mid/10 px-4 py-3 text-center text-sm font-bold text-ocean-mid">
                        <Users className="h-4 w-4" />
                        Restam {selected.vagas} vagas disponíveis em {String(selected.d).padStart(2, "0")}/{String(view.m + 1).padStart(2, "0")}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs font-medium text-brand-navy/60">
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-white ring-1 ring-ocean-mid/40" /> Disponível</span>
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-amber-200 ring-1 ring-amber-400" /> Poucas vagas</span>
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-red-600" /> Lotado</span>
              </div>

              <a href={CONTACT.whatsappFloatLink} target="_blank" rel="noreferrer" className="mt-4 flex items-center justify-center gap-2 text-sm font-bold text-brand-navy hover:text-ocean-mid" data-testid="availability-contact">
                <MessageCircle className="h-4 w-4 text-[#25D366]" />
                A data que procura está com poucas vagas ou lotada? Fale conosco
              </a>
            </div>
          </Reveal>
        </div>

        {/* CTAs below the calendar */}
        <Reveal delay={0.15} className="mt-10">
          <div className="mx-auto flex max-w-xl flex-col items-center gap-3">
            <button
              onClick={() => openLead("Seção Agenda")}
              className="cta-sun animate-pulse-glow inline-flex w-full items-center justify-center rounded-full px-8 py-5 text-base font-extrabold uppercase tracking-wide text-white shadow-2xl shadow-sun/30 sm:text-lg"
              data-testid="availability-cta"
            >
              Garantir ingresso na promoção
            </button>
            <a
              href={CONTACT.whatsappFloatLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-sm font-extrabold uppercase tracking-wide text-white shadow-xl shadow-[#25D366]/30 transition-transform hover:scale-[1.02] sm:text-base"
              data-testid="availability-whatsapp-cta"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.15-.174.2-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.412-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.463 3.488" />
              </svg>
              Já quero garantir minha data e comprar os ingressos
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
